import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { useRealtimeCalendar } from '../../hooks/useRealtime';
import './AdminCalendarRequests.css';

const AdminCalendarRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const intervalRef = useRef(null);

    const [activeId, setActiveId] = useState(null);

    // store inputs per active row
    const [formData, setFormData] = useState({
        session_time: '',
        meeting_link: ''
    });

    // Real-time: Refresh when new calendar request arrives
    useRealtimeCalendar((data) => {
        console.log('New calendar request:', data);
        loadRequests();
    });

    // Load directly from Supabase
    const loadRequests = async () => {
        try {
            const { data, error } = await supabase
                .from('calendar_requests')
                .select('*')
                .order('created_at', { ascending: false });
            
            if (error) throw error;
            
            setRequests(data || []);
            setError('');
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error(err);
            setError('Failed to load calendar requests');
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRequests();
        intervalRef.current = setInterval(loadRequests, 10000);
        return () => clearInterval(intervalRef.current);
    }, []);

    const openApproveBox = (req) => {
        setActiveId(req.id);
        setFormData({
            session_time: req.session_time || '',
            meeting_link: req.meeting_link || ''
        });
    };

    const approveRequest = async (id) => {
        if (!formData.session_time || !formData.meeting_link) {
            alert('Please enter session time and meeting link');
            return;
        }

        try {
            const { error } = await supabase
                .from('calendar_requests')
                .update({
                    status: 'approved',
                    session_time: formData.session_time,
                    meeting_link: formData.meeting_link
                })
                .eq('id', id);
            
            if (error) throw error;

            setActiveId(null);
            setFormData({ session_time: '', meeting_link: '' });
            loadRequests();
        } catch {
            alert('Approval failed');
        }
    };

    const rejectRequest = async (id) => {
        try {
            const { error } = await supabase
                .from('calendar_requests')
                .update({ status: 'rejected' })
                .eq('id', id);
            
            if (error) throw error;
            
            loadRequests();
        } catch {
            alert('Rejection failed');
        }
    };

    return (
        <div className="admin-calendar-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h1 className="admin-title" style={{ margin: 0 }}>Calendar Booking Requests</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {lastUpdated && (
                        <span style={{ fontSize: '12px', color: '#888' }}>
                            🟢 Live · Last updated: {lastUpdated}
                        </span>
                    )}
                    <button
                        onClick={loadRequests}
                        style={{ padding: '6px 14px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                    >
                        ↻ Refresh
                    </button>
                </div>
            </div>

            {loading && <p className="loading-text">Loading requests...</p>}
            {error && <p className="loading-text" style={{ color: '#c62828' }}>{error}</p>}

            {!loading && requests.length === 0 && (
                <p className="empty-text">No booking requests found.</p>
            )}

            {!loading && requests.length > 0 && (
                <table className="admin-calendar-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Notes</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {requests.map(r => (
                            <tr key={r.id}>
                                <td>
                                    <strong>{r.full_name}</strong>
                                    <div className="email">{r.email}</div>
                                </td>

                                <td>{r.booking_date}</td>
                                <td className="type">{r.booking_type}</td>
                                <td className="notes">{r.notes || '-'}</td>

                                <td>
                                    <span className={`status-badge ${r.status}`}>
                                        {r.status}
                                    </span>

                                    {/* 🔹 show approved info */}
                                    {r.status === 'approved' && (
                                        <div className="approved-info">
                                            <small>{r.session_time}</small>
                                            <br />
                                            <a href={r.meeting_link} target="_blank" rel="noreferrer">
                                                Meeting Link
                                            </a>
                                        </div>
                                    )}
                                </td>

                                <td>
                                    {r.status === 'pending' ? (
                                        activeId === r.id ? (
                                            <div className="approve-box">
                                                <input
                                                    type="text"
                                                    placeholder="Session Time (e.g. 6 PM)"
                                                    value={formData.session_time}
                                                    onChange={e =>
                                                        setFormData({
                                                            ...formData,
                                                            session_time: e.target.value
                                                        })
                                                    }
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Meeting Link"
                                                    value={formData.meeting_link}
                                                    onChange={e =>
                                                        setFormData({
                                                            ...formData,
                                                            meeting_link: e.target.value
                                                        })
                                                    }
                                                />

                                                <button
                                                    className="btn approve"
                                                    onClick={() => approveRequest(r.id)}
                                                >
                                                    Confirm
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="action-buttons">
                                                <button
                                                    className="btn approve"
                                                    onClick={() => openApproveBox(r)}
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    className="btn reject"
                                                    onClick={() => rejectRequest(r.id)}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )
                                    ) : (
                                        <span className="no-action">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default AdminCalendarRequests;
